import type { ModelName } from "../../../types";

/** The actions whose auto-sync behavior an integration can gate. */
export type SyncAction = "create" | "update" | "delete";

/** Whether auto-sync fires on each action for a single model. */
export type SyncBehavior = Record<SyncAction, boolean>;

/**
 * Per-model auto-sync summary. A model key is present only when at least one
 * integration in the source list affects it; absent keys mean "no integration
 * touches this model" (callers should fall back to enabled).
 */
export type IntegrationSyncMap = Partial<Record<ModelName, SyncBehavior>>;

/** Minimal integration shape this map needs off each integration. */
export interface IntegrationLike {
  title: string;
  disableEnduserAutoSync?: boolean;
  disableTicketAutoSync?: boolean;
}

const HEALTHIE_TITLE = "Healthie";
const ELATION_TITLE = "Elation";

const SYNC_MODELS: ModelName[] = ["endusers", "tickets"];

/** `delete` mirrors `update` — an integration that syncs updates also syncs deletes. */
const behavior = (create: boolean, update: boolean): SyncBehavior => ({ create, update, delete: update });

/**
 * Per-integration rule: the partial sync behavior an integration contributes.
 * Only Healthie and Elation honor the `disable*AutoSync` flags — every other
 * integration title is unlisted and contributes nothing.
 */
const RULES: Record<string, (integration: IntegrationLike) => IntegrationSyncMap> = {
  [HEALTHIE_TITLE]: (integration) => ({
    // Healthie gates only on creation; there is no flag-gated update/delete sync.
    endusers: behavior(!integration.disableEnduserAutoSync, false),
    tickets: behavior(!integration.disableTicketAutoSync, false),
  }),
  [ELATION_TITLE]: (integration) => ({
    // Updates always sync for endusers already linked in Elation; disabling only
    // blocks creating brand-new (unlinked) patients. Reported true since an update
    // (or delete) sync can still happen. Elation never auto-syncs tickets.
    endusers: behavior(!integration.disableEnduserAutoSync, true),
  }),
};

/**
 * Reduces a list of integrations into a per-model auto-sync summary, combining
 * integrations with OR — a model/action is `true` if **any** integration will
 * sync it. A model key is only present when some integration affects it.
 *
 * @param integrations The org's (redacted) integrations.
 * @returns An {@link IntegrationSyncMap} keyed only by affected models.
 */
export const buildIntegrationSyncMap = (integrations: IntegrationLike[]): IntegrationSyncMap =>
  integrations.reduce<IntegrationSyncMap>((acc, integration) => {
    const rule = RULES[integration.title];
    if (!rule) return acc;

    const contribution = rule(integration);
    return SYNC_MODELS.reduce((map, model) => {
      const contributed = contribution[model];
      if (!contributed) return map;

      const existing = map[model];
      map[model] = existing
        ? {
            create: existing.create || contributed.create,
            update: existing.update || contributed.update,
            delete: existing.delete || contributed.delete,
          }
        : contributed;
      return map;
    }, acc);
  }, {});
